// ==============================================
// Pieces the App Library page and its "Add an app" dialog share: the Scout
// shelves, small formatters, and the multipart upload of an APK set.
// ==============================================
import { api } from '../../services/api';

/**
 * Which shelf an app sits on: what a Scout USER may do with it. A different
 * question from which plans get it, and from who serves the file.
 */
export type Availability = 'skipper' | 'admin_only' | 'withheld';

export const SHELVES: Array<{ value: Availability; label: string; blurb: string }> = [
  { value: 'skipper', label: 'Open to the skipper', blurb: 'Installed, and anyone aboard can put it on a tile.' },
  { value: 'admin_only', label: 'Admin only', blurb: 'Installed and kept current, but invisible to the skipper. Only an admin can place it.' },
  { value: 'withheld', label: 'Not on Scouts yet', blurb: 'Held in the portal. Never sent to a boat, and no one aboard can see it.' },
];
export const shelfOf = (a: Availability) => SHELVES.find((s) => s.value === a) ?? SHELVES[2];

export const mb = (n: number) => `${(n / 1024 / 1024).toFixed(n > 100 * 1024 * 1024 ? 0 : 1)} MB`;
export const errText = (e: any, fallback: string) => e?.response?.data?.error || e?.message || fallback;

export interface UploadFile { name: string; file: File; sha256: string }

/** Where an upload is. `retryingPart` is set while a part waits for another try. */
export interface UploadProgress { file: string; pct: number; retryingPart?: number }

export const progressText = (p: UploadProgress) =>
  p.retryingPart ? `Retrying part ${p.retryingPart} of ${p.file}…` : `Uploading ${p.file} · ${p.pct}%`;

/**
 * Waits before each retry of a part. Three retries after the first try, so a
 * part gets four chances over about 22 seconds before the upload gives up.
 * A dropped request from a marina connection (Nouméa, 14 Oct) is the case.
 */
export const RETRY_DELAYS_MS = [2000, 5000, 15000];

export interface UploadOptions {
  /** Injected by tests; real uploads wait on a timer. */
  sleep?: (ms: number) => Promise<void>;
  delays?: number[];
}

const realSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** Worth another try: no answer at all (dropped, timed out), or the server or an edge hiccuped. */
function retryable(e: any): boolean {
  const status = e?.response?.status;
  return status === undefined || status === 408 || status === 429 || status >= 500;
}

/** The upload gave up for good. The draft is kept, so picking the same files again resumes it. */
export class UploadStoppedError extends Error {}

async function withRetries<T>(what: () => Promise<T>, onRetry: () => void, opts: UploadOptions): Promise<T> {
  const delays = opts.delays ?? RETRY_DELAYS_MS;
  const sleep = opts.sleep ?? realSleep;
  for (let attempt = 0; ; attempt++) {
    try {
      return await what();
    } catch (e) {
      if (attempt >= delays.length || !retryable(e)) throw e;
      onRetry();
      await sleep(delays[attempt]);
    }
  }
}

const stopped = (where: string) => new UploadStoppedError(
  `The upload stopped: ${where} would not go through after several tries. It is saved as unfinished. Choose the same files again to resume it.`,
);

/** A version stuck in draft with files still to land: what "resume" works from. */
export interface DraftVersion {
  id: string; versionCode: number; state: string;
  files: Array<{ name: string; sha256: string; size: number; uploaded: boolean }>;
}

/** The version with this code that stopped part way through its upload, or null. */
export function findStuckDraft<V extends DraftVersion>(versions: V[], code: number | null): V | null {
  if (code === null) return null;
  return versions.find((v) => v.versionCode === code && v.state === 'draft' && !(v.files.length > 0 && v.files.every((f) => f.uploaded))) ?? null;
}

/** Are these the same files the stuck draft was registered with (names, sizes and sha256)? */
export function sameFilesAsDraft(draft: DraftVersion, files: UploadFile[]): boolean {
  if (draft.files.length !== files.length) return false;
  return draft.files.every((d) => {
    const f = files.find((x) => x.name === d.name);
    return !!f && f.file.size === d.size && f.sha256.toLowerCase() === d.sha256.toLowerCase();
  });
}

/** Stream the given files of a draft version to R2 in parts, each part retried before giving up. */
async function uploadFiles(versionId: string, files: UploadFile[], onProgress: (p: UploadProgress) => void, opts: UploadOptions): Promise<void> {
  for (const f of files) {
    onProgress({ file: f.name, pct: 0 });
    const start = await withRetries(() => api.adminAppLibraryStartUpload(versionId, f.name), () => undefined, opts)
      .catch((e) => { throw retryable(e) ? stopped(`starting ${f.name}`) : e; });
    const parts: Array<{ part_number: number; etag: string }> = [];
    for (let off = 0, n = 1; off < f.file.size; off += start.part_size, n++) {
      const chunk = f.file.slice(off, Math.min(f.file.size, off + start.part_size));
      const pct = Math.round(Math.min(100, (off / f.file.size) * 100));
      const part = await withRetries(
        () => api.adminAppLibraryUploadPart(versionId, f.name, start.upload_id, n, chunk),
        () => onProgress({ file: f.name, pct, retryingPart: n }),
        opts,
      ).catch((e) => { throw retryable(e) ? stopped(`part ${n} of ${f.name}`) : e; });
      parts.push(part);
      onProgress({ file: f.name, pct: Math.round(Math.min(100, ((off + chunk.size) / f.file.size) * 100)) });
    }
    await withRetries(() => api.adminAppLibraryCompleteUpload(versionId, f.name, start.upload_id, parts), () => undefined, opts)
      .catch((e) => { throw retryable(e) ? stopped(`finishing ${f.name}`) : e; });
  }
}

/**
 * Create a version from an APK set and stream each file to R2 in parts. The
 * version starts in Review once every file is complete. If the upload stops
 * for good the version stays a draft that `resumeApkSetVersion` can finish.
 */
export async function uploadApkSetVersion(
  appId: string,
  meta: { versionName: string; versionCode: number; note: string | null; required: boolean },
  files: UploadFile[],
  onProgress: (p: UploadProgress) => void,
  opts: UploadOptions = {},
): Promise<void> {
  const version: { id: string } = await api.adminAppLibraryCreateVersion(appId, {
    version_name: meta.versionName, version_code: meta.versionCode, note: meta.note, required: meta.required,
    files: files.map((f) => ({ name: f.name, size: f.file.size, sha256: f.sha256 })),
  });
  await uploadFiles(version.id, files, onProgress, opts);
}

/**
 * Finish a stuck draft with the same files: the files that already landed are
 * skipped, each one that did not starts a fresh multipart upload on the same
 * version, and the version moves to Review as normal when the last lands.
 */
export async function resumeApkSetVersion(
  draft: DraftVersion,
  files: UploadFile[],
  onProgress: (p: UploadProgress) => void,
  opts: UploadOptions = {},
): Promise<void> {
  const pending = files.filter((f) => !draft.files.find((d) => d.name === f.name)?.uploaded);
  await uploadFiles(draft.id, pending, onProgress, opts);
}
